import React, { HTMLProps, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import Toggle from 'react-toggle';
import { ForecastResponse } from '../../../../api/robotapi';
import { PRIVACY_URL, ROBOT_ENVIRONMENT } from '../../../../config/constants';
import { IconElements } from '../../../../config/icons';
import { ForecastContext, ForecastParameters } from '../../../../contexts/investment-robot/ForecastContext';
import { RobotEnvironmentContext } from '../../../../contexts/general/RobotEnvironmentContext';
import {
    GoalIndicationContext,
    GoalIndicationParameters,
} from '../../../../contexts/investment-robot/GoalIndicationContext';
import { getInvestmentToolDirectLink } from '../../../../utils/functions';
import { validateContactDetailsInput, validateEmail, validateName, validatePhone } from '../../../../utils/validators';
import { Button } from '../../../general/Button';
import { ContactDetailsContext } from '../../../../contexts/general/ContactDetailsContext';

const emailHookURL = 'https://hooks.zapier.com/hooks/catch/4760236/ojt53tq/';

export type ContactDetails = { 
    name: string;
    email: string;
    phone: string;
    advisorEmail: string;
    call: boolean;
    validInput: boolean;
};

export const ForecastEmailMeCard: React.FC<{ onClose?: () => void; setIsEmailSent?: (isEmailSent : boolean) => void } & HTMLProps<HTMLDivElement>> = ({
    onClose,
    setIsEmailSent,
    className,
}) => {
    const { forecast } = useContext(ForecastContext);
    const { environment } = useContext(RobotEnvironmentContext);
    const { parameters: goalIndicationParameters } = useContext(GoalIndicationContext);
    const { t } = useTranslation('investment');
    const { contactDetails } = useContext(ContactDetailsContext);

    const sendEmailMutation = useMutation<
        boolean,
        unknown,
        {
            environment: ROBOT_ENVIRONMENT;
            contactDetails: ContactDetails;
            mayCall: boolean;
            goalIndication: GoalIndicationParameters;
            parameters: ForecastParameters;
            forecast: ForecastResponse;
            link: string;
            metadata: { date: number; tool: 'investment' | 'funds'; source: string };
        }
    >(async (call) => {
        const validation = validInput();

        if (!validation) throw Error('Input not valid');

        const response = await fetch(emailHookURL, {
            method: 'POST',
            body: JSON.stringify(call),
        });

        if (!response.ok) throw Error('Request not succesful');
        setIsEmailSent && setIsEmailSent(true);
        onClose && onClose();
        return response.ok;
    });

    const validInput = () => {
        return contactDetails.validInput;
    }

    const isAdvisor = () => {
        return environment === 'Advisor';
    }

    const EmailSuccesMessage = () => { 
        if (sendEmailMutation.isSuccess) {
            return <span className=" text-bnd-green">
                {t('forecast-screen-email-me-succes-message')}
            </span>
        }

        return <></>;
    }

    const EmailErrorMessage = () => { 
        if (sendEmailMutation.isError) {
            <span className=" text-red-500 overflow-ellipsis">
                {t('forecast-screen-email-me-fail-message')}
            </span>
        }

        return <></>;
    }

    const sendEmail = async function(){
        await sendEmailMutation.mutate({
            environment: environment,
            mayCall: isAdvisor() ? false : contactDetails.call,
            contactDetails: contactDetails,
            parameters: forecast.parameters,
            forecast: forecast.query.data!,
            goalIndication: goalIndicationParameters,
            link: getInvestmentToolDirectLink(
                forecast.parameters,
                goalIndicationParameters.selectedGoal,
            ),
            metadata: {
                date: Date.now(),
                source: window.location.toString(),
                tool: 'investment',
            },
        });
    }

    return (
        <div className={`flex flex-col bg-bnd-white-1 p-8 shadow-2xl sm:rounded-2xl font-light ${className}`}>
            <div className="flex flex-row justify-between items-center">
                <div className="text-2xl font-semibold">{t('forecast-screen-email-me-title')}</div>
                {onClose && (
                    <div>
                        <button
                            className="ml-8 text-2xl flex items-center focus:outline-none"
                            onClick={() => {
                                onClose();
                            }}
                        >
                            <IconElements.Cross className="text-bnd-green w-6 h-6 sm:h-8 sm:w-8 hover:text-bnd-green-hover transition" />
                        </button>
                    </div>
                )}
            </div>
            <span className="mt-6 mb-6">{t('forecast-screen-email-me-subtitle')}</span>
            <ForecastEmailMeInputs/>
            <div className="flex flex-col sm:flex-row mt-6 gap-4 justify-center sm:justify-start items-center">
                <Button
                    className={`px-16 ${
                        validInput() === false
                            ? 'opacity-40 cursor-not-allowed'
                            : ''
                    }`}
                    tint="green"
                    disabled={
                        validInput() &&
                        forecast.query.data === undefined
                    }
                    onClick = { () => {sendEmail()}}
                >
                    <span className="text-center">{t('forecast-screen-email-me-buttton-text')}</span>
                </Button>
                <EmailSuccesMessage/>
                <EmailErrorMessage/>
            </div>
        </div>
    );
};



const DisclaimerToggle: React.FC<
    { isChecked: boolean; onChange: React.ChangeEventHandler<HTMLInputElement> | undefined } & Omit<
        HTMLProps<HTMLDivElement>,
        'onChange'
    >
> = ({ isChecked, className, onChange }) => {
    const { t } = useTranslation('investment');
    return (
        <div className={`flex flex-col ${className}`}>
            <label className="inline-flex items-start mb-3">
                <Toggle defaultChecked={isChecked} icons={false} onChange={onChange} />
                <span className={`ml-4 transition-opacity ${isChecked === false ? 'opacity-50' : ''}`}>
                    {t('forecast-screen-email-me-option')}
                </span>
            </label>
        </div>
    );
};

const EmailMeTextInput: React.FC<
    {
        placeholder?: string;
        isValid: boolean;
        value: string;
        onChange: (input: string) => void;
    } & Omit<HTMLProps<HTMLDivElement>, 'value' | 'onChange'>
> = ({ placeholder, isValid, value, onChange, className, ...rest }) => {
    return (
        <div
            className={`relative flex flex-row items-center bg-white border border-gray-200 rounded-lg pr-4 ${
                isValid === true ? 'border border-bnd-green' : ''
            } ${className}`}
            {...rest}
        >
            <input
                className={`flex-1 p-2 focus:outline-none rounded-lg font-light`}
                value={value}
                type="Text"
                placeholder={placeholder}
                onChange={(event) => {
                    onChange(event.target.value);
                }}
            ></input>
            <div className="absolute top-0 bottom-0 right-2 flex items-center justify-center">
                <IconElements.Check
                    className={`transition-opacity ${isValid === true ? '' : 'opacity-0'} text-bnd-green w-3 h-4`}
                />
            </div>
        </div>
    );
};


export const ForecastEmailMeInputs = () => {
    const { environment } = useContext(RobotEnvironmentContext);
    const { contactDetails, setContactDetails } = useContext(ContactDetailsContext);
    const { actions } = useContext(GoalIndicationContext);

    const { t } = useTranslation('investment');

    const isAdvisor = () => {
        return environment === 'Advisor';
    }

    const PrivacyStatement = () => { 
        return <span className="mt-4">
            In ons{' '}
            <a className="underline text-bnd-green" href={PRIVACY_URL} target="_blank" rel="noreferrer">
                privacystatement
            </a>{' '}
            leest u hoe wij met uw persoonsgegevens omgaan.
        </span>
    }

    const getName = () => {
        return contactDetails.name;
    }

    const getClientEmail = () => {
        return contactDetails.email;
    }

    const getClientPhone = () => {
        return contactDetails.phone;
    }

    const getClientAdvisorEmail = () => {
        return contactDetails.advisorEmail;
    }

    const getCallChecked = () => {
        return contactDetails.call;
    }

    const getIsValid = ( newClientDetailValues: ContactDetails ) => {
        return validateContactDetailsInput(newClientDetailValues, isAdvisor());
    }

    const oldFormValues = {
        name: getName(),
        email: getClientEmail(),
        phone: getClientPhone(),
        advisorEmail: getClientAdvisorEmail(),
        call: getCallChecked(),
        // Only used bt GoalQuestion to check for next step. ValidInput is overwritten in the set functions
        validInput: false,
    }

    const setNewStringValue = (key: string, value: string) => {
        var contactDetails = {...oldFormValues, [key]:value}
        updateContexts(contactDetails)
    }

    const setNewBooleanValue = (key: string, value: boolean) => {
        var contactDetails = {...oldFormValues, [key]:value}
        updateContexts(contactDetails);
    }

    const updateContexts = (contactDetails: ContactDetails) => {
        contactDetails.validInput = getIsValid(contactDetails);
        if (setContactDetails)
            setContactDetails(contactDetails);
        actions.setParameters({ contactDetailsValid: contactDetails.validInput });
    }

    

    return (
        <div>
            <div className="flex-1 flex flex-col justify-between">
                <EmailMeTextInput
                    value={getName()}
                    isValid={validateName(getName())}
                    placeholder={t('forecast-screen-email-me-name-field-placeholder')}
                    onChange={(value) => {
                        setNewStringValue('name', value)
                    }}
                />
                <EmailMeTextInput
                    className="mt-2"
                    value={getClientEmail()}
                    isValid={validateEmail(getClientEmail())}
                    placeholder={t('forecast-screen-email-me-email-field-placeholder')}
                    onChange={(value) => {
                        setNewStringValue('email', value)
                    }}
                />
                { !isAdvisor() &&
                    <EmailMeTextInput
                        className="mt-2"
                        value={getClientPhone()}
                        isValid={validatePhone(getClientPhone())}
                        placeholder={t('forecast-screen-email-me-phone-field-placeholder')}
                        onChange={(value) => {
                            setNewStringValue('phone', value)
                        }}
                    />
                }
                { isAdvisor() &&
                    <EmailMeTextInput
                        className="mt-2"
                        value={getClientAdvisorEmail()}
                        isValid={validateEmail(getClientAdvisorEmail())}
                        placeholder={t('forecast-screen-email-me-email-advidor-field-placeholder')}
                        onChange={(value) => {
                            setNewStringValue('advisorEmail', value)
                        }}
                    />
                }
            </div>
            { !isAdvisor() &&
                <DisclaimerToggle
                    className="flex-1 mt-4"
                    isChecked={getCallChecked()}
                    onChange={(event) => {
                        const checked = event.target.checked
                        setNewBooleanValue("call", checked);
                    }}
                />
            }
            <PrivacyStatement/>
        </div>
    );
};